import { Anchor, Card, Center, Group, Image, Stack, Text } from '@mantine/core';
import { IconArrowRight } from '@tabler/icons-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
  type BrowserLoginSuccess,
  LOGIN_METHOD,
  type LoginChallenge,
  type LoginChallengeResult,
  type LoginChallengeUserResponse,
  type LoginRequest,
  type Verify2FALoginRequest,
} from '@/api';
import { Login } from '../components/Login/Login';
import { useLanguageAwareRouting } from '../hooks/useLanguageAwareRouting';

export interface LoginPageProps {
  // Required props for login functionality
  login: (loginRequest: LoginRequest) => Promise<LoginChallenge>;
  loginChallenge: (props: LoginChallengeUserResponse) => Promise<LoginChallengeResult>;
  verify2FA: (request: Verify2FALoginRequest) => Promise<BrowserLoginSuccess>;

  // Navigation
  onLoginSuccess?: () => void; // Defaults to '/account'
  signupRoute?: string; // Default: '/signup'
  privacyRoute?: string; // Default: '/privacy'
  termsRoute?: string; // Default: '/terms'

  // Brand customization (defaults work for all current apps)
  logo?: ReactNode; // Default: <img src="/logo.svg" />
  logoSrc?: string; // Default: '/logo.svg'
  brandName?: ReactNode; // Default: t('brand.name') + t('brand.nameSecond')

  // Other
  showSignupButton?: boolean; // Default: true
  loginMethods?: LOGIN_METHOD[];
}

export function LoginPage(props: LoginPageProps) {
  const { t } = useTranslation();
  const [hasNewAccountButton, setHasNewAccountButton] = useState(props.showSignupButton !== false);
  const nav = useNavigate();
  const { createLanguageURL } = useLanguageAwareRouting();

  const {
    login,
    loginChallenge,
    verify2FA,
    onLoginSuccess,
    signupRoute = '/signup',
    privacyRoute = '/privacy',
    termsRoute = '/terms',
    logoSrc = '/logo.svg',
    loginMethods = [
      LOGIN_METHOD.NOSTR,
      LOGIN_METHOD.GITHUB,
      LOGIN_METHOD.GOOGLE,
      LOGIN_METHOD.EMAIL_MAGIC_LINK,
      LOGIN_METHOD.MICROSOFT,
    ],
  } = props;

  const loginCb = async (loginRequest: LoginRequest) => {
    setHasNewAccountButton(false);
    return login(loginRequest);
  };

  const loginDoneCb = () => {
    if (onLoginSuccess) {
      onLoginSuccess();
    } else {
      nav(createLanguageURL('/account'));
    }
  };

  const brandName = props.brandName;

  const logo = props.logo ?? <Image src={logoSrc} alt="Logo" w={48} h={48} fit="contain" />;

  return (
    <Center mih="100vh" py="xl">
      <Stack align="center" gap="lg" w="100%" maw={420} px="md">
        <Stack align="center" gap="xs">
          {logo}
          {brandName && (
            <Text fw={600} size="lg">
              {brandName}
            </Text>
          )}
        </Stack>

        <Card withBorder radius="md" p="lg" w="100%" shadow="xs">
          <Login
            login={loginCb}
            loginChallenge={loginChallenge}
            verify2FA={verify2FA}
            loginDoneCb={loginDoneCb}
            loginMethods={loginMethods}
          />
        </Card>

        <Stack align="center" gap="sm">
          {hasNewAccountButton && (
            <Group gap="xs" align="center">
              <Text size="sm" c="dimmed">
                {t('auth.newHere')}
              </Text>
              <Anchor component={Link} to={signupRoute} size="sm" fw={500}>
                <Group gap={4} align="center" wrap="nowrap">
                  {t('auth.howToSignup')}
                  <IconArrowRight size={14} />
                </Group>
              </Anchor>
            </Group>
          )}
          <Group gap="lg">
            <Anchor component={Link} to={privacyRoute} size="xs" c="dimmed">
              {t('auth.privacyPolicy')}
            </Anchor>
            <Anchor component={Link} to={termsRoute} size="xs" c="dimmed">
              {t('auth.termsOfService')}
            </Anchor>
          </Group>
        </Stack>
      </Stack>
    </Center>
  );
}
